
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Image as ImageIcon, X, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PhotoGalleryProps {
  photos: Array<{
    url: string;
    caption?: string;
  }>;
  className?: string;
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ photos, className = '' }) => {
  const [selectedPhoto, setSelectedPhoto] = useState<number | null>(null);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const validPhotos = photos.filter(photo => photo.url && photo.url.trim() !== '');

  if (validPhotos.length === 0) {
    return null;
  }

  const openLightbox = (index: number) => {
    setSelectedPhoto(index);
  };

  const closeLightbox = () => {
    setSelectedPhoto(null);
  };

  const goToPrevious = () => {
    if (selectedPhoto === null) return;
    setSelectedPhoto(selectedPhoto === 0 ? validPhotos.length - 1 : selectedPhoto - 1);
  };

  const goToNext = () => {
    if (selectedPhoto === null) return;
    setSelectedPhoto(selectedPhoto === validPhotos.length - 1 ? 0 : selectedPhoto + 1);
  };

  // Clean and validate URLs
  const normalizeUrl = (url: string): string => {
    let cleanUrl = url.trim();
    
    console.log('Original URL:', url);
    
    // Handle protocol-relative URLs
    if (cleanUrl.startsWith('//')) {
      cleanUrl = 'https:' + cleanUrl;
    } else if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = 'https://' + cleanUrl;
    }
    
    console.log('Normalized URL:', cleanUrl);
    return cleanUrl;
  };

  // Check if URL is likely a direct image
  const isDirectImage = (url: string): boolean => {
    const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg|bmp|tiff)(\?.*)?$/i;
    return imageExtensions.test(url);
  };

  // Try to get direct image URL from common services
  const getDirectImageUrl = (url: string): string => {
    console.log('Processing URL for direct image:', url);
    
    // For Pexels, try to extract photo ID and construct direct URL
    if (url.includes('pexels.com/photo/')) {
      const match = url.match(/pexels\.com\/photo\/[^/]+-(\d+)/);
      if (match) {
        const photoId = match[1];
        // Note: This would ideally use Pexels API, but for now we'll try the original URL
        console.log('Detected Pexels photo ID:', photoId);
      }
    }
    
    // For now, return the normalized URL as-is
    // Users should provide direct image URLs for best results
    return normalizeUrl(url);
  };

  const handleImageLoad = (url: string) => {
    console.log('✅ Image loaded successfully:', url);
    setLoadedImages(prev => new Set(prev).add(url));
    setFailedImages(prev => {
      const newSet = new Set(prev);
      newSet.delete(url);
      return newSet;
    });
  };

  const handleImageError = (url: string, event: any) => {
    console.error('❌ Image failed to load:', url);
    console.error('Error details:', event);
    setFailedImages(prev => new Set(prev).add(url));
    setLoadedImages(prev => {
      const newSet = new Set(prev);
      newSet.delete(url);
      return newSet;
    });
  };

  const isImageFailed = (url: string) => failedImages.has(url);

  // Get filename for display
  const getImageName = (url: string) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop() || 'image';
      return filename.length > 30 ? filename.substring(0, 30) + '...' : filename;
    } catch {
      return url.length > 30 ? url.substring(0, 30) + '...' : url;
    }
  };

  return (
    <>
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`}>
        {validPhotos.map((photo, index) => {
          const imageUrl = getDirectImageUrl(photo.url);
          const isDirect = isDirectImage(imageUrl);
          
          console.log(`Photo ${index + 1}:`, {
            original: photo.url,
            processed: imageUrl,
            isDirect,
            failed: isImageFailed(imageUrl)
          });
          
          return (
            <Card key={index} className="bg-gray-800 border-gray-700 overflow-hidden cursor-pointer hover:ring-2 hover:ring-sports-green transition-all">
              <CardContent className="p-0">
                {!isImageFailed(imageUrl) && isDirect ? (
                  <div className="relative">
                    <img
                      src={imageUrl}
                      alt={photo.caption || `Photo ${index + 1}`}
                      className="w-full h-48 object-cover"
                      onClick={() => openLightbox(index)}
                      onLoad={() => handleImageLoad(imageUrl)}
                      onError={(e) => handleImageError(imageUrl, e)}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition-all flex items-center justify-center opacity-0 hover:opacity-100">
                      <div className="text-white text-sm font-medium">Click to enlarge</div>
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-48 bg-gray-700 flex items-center justify-center">
                    <div className="text-center p-4">
                      <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      {isImageFailed(imageUrl) ? (
                        <>
                          <p className="text-gray-400 text-xs mb-2">Failed to load image</p>
                          <p className="text-gray-500 text-xs break-all mb-2">
                            {getImageName(imageUrl)}
                          </p>
                        </>
                      ) : !isDirect ? (
                        <>
                          <p className="text-gray-400 text-xs mb-2">Not a direct image URL</p>
                          <p className="text-gray-500 text-xs break-all mb-2">
                            Please use direct image links (.jpg, .png, etc.)
                          </p>
                        </>
                      ) : (
                        <p className="text-gray-400 text-xs mb-2">Image unavailable</p>
                      )}
                      <a 
                        href={photo.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sports-green hover:text-green-400 text-xs"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="h-3 w-3" />
                        Open Original
                      </a>
                    </div>
                  </div>
                )}
                {photo.caption && (
                  <div className="p-3">
                    <p className="text-gray-300 text-sm">{photo.caption}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Lightbox Modal */}
      {selectedPhoto !== null && !isImageFailed(getDirectImageUrl(validPhotos[selectedPhoto].url)) && isDirectImage(getDirectImageUrl(validPhotos[selectedPhoto].url)) && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-full">
            <Button
              onClick={closeLightbox}
              variant="outline"
              size="sm"
              className="absolute top-4 right-4 z-10 bg-black/50 border-gray-600 text-white hover:bg-black/70"
            >
              <X className="h-4 w-4" />
            </Button>
            
            {validPhotos.length > 1 && (
              <>
                <Button
                  onClick={goToPrevious}
                  variant="outline"
                  size="sm"
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 border-gray-600 text-white hover:bg-black/70"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <Button
                  onClick={goToNext}
                  variant="outline"
                  size="sm"
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 border-gray-600 text-white hover:bg-black/70"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
            
            <img
              src={getDirectImageUrl(validPhotos[selectedPhoto].url)}
              alt={validPhotos[selectedPhoto].caption || `Photo ${selectedPhoto + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            
            {validPhotos[selectedPhoto].caption && (
              <div className="absolute bottom-4 left-4 right-4 bg-black/70 text-white p-3 rounded-lg">
                <p className="text-center">{validPhotos[selectedPhoto].caption}</p>
              </div>
            )}
            
            <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-lg text-sm">
              {selectedPhoto + 1} / {validPhotos.length}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PhotoGallery;
